import React, { useEffect, useRef, useState } from 'react';
import type { CatalogItem } from '../../services/restaurantService';
import './Catalog.css';

function catalogLabel(item: CatalogItem): string {
  return item.name || item.type || '';
}

function catalogInitial(item: CatalogItem): string {
  const label = catalogLabel(item);
  return label ? label.charAt(0).toUpperCase() : '?';
}

const PLACEHOLDER_PALETTES = [
  { bg: '#FFE0B2', color: '#E65100' },
  { bg: '#C8E6C9', color: '#2E7D32' },
  { bg: '#BBDEFB', color: '#1565C0' },
  { bg: '#F8BBD0', color: '#880E4F' },
  { bg: '#D1C4E9', color: '#4527A0' },
  { bg: '#B2EBF2', color: '#00695C' },
  { bg: '#FFF9C4', color: '#F57F17' },
  { bg: '#FFCCBC', color: '#BF360C' },
];

function catalogPlaceholderStyle(item: CatalogItem): { background: string; color: string } {
  const label = catalogLabel(item);
  let hash = 0;
  for (let i = 0; i < label.length; i++) hash = label.charCodeAt(i) + ((hash << 5) - hash);
  const { bg, color } = PLACEHOLDER_PALETTES[Math.abs(hash) % PLACEHOLDER_PALETTES.length];
  return { background: bg, color };
}

interface CatalogCategoryGridProps {
  title: string;
  items: CatalogItem[];
  onItemClick?: (item: CatalogItem) => void;
}

const CatalogCategoryGrid: React.FC<CatalogCategoryGridProps> = ({ title, items, onItemClick }) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [wrapRows, setWrapRows] = useState(false);

  // The 2-row swipe layout fills columns top-then-bottom, which leaves gaps when the items would
  // fit in two rows anyway. In that case fill the first row before wrapping instead.
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return undefined;
    const measure = () => {
      const style = window.getComputedStyle(el);
      const col = parseFloat(style.getPropertyValue('--catalog-grid-col')) || 88;
      const gap = parseFloat(style.columnGap) || 0;
      const perRow = Math.max(1, Math.floor((el.clientWidth + gap) / (col + gap)));
      setWrapRows(items.length <= perRow * 2);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [items.length]);

  if (!items.length) return null;

  return (
    <section className="catalog-grid-section" aria-label={title}>
      <h2 className="catalog-grid-title">{title}</h2>
      <div ref={gridRef} className={`catalog-grid${wrapRows ? ' catalog-grid--wrap' : ''}`} role="list">
        {items.map((item) => {
          const label = catalogLabel(item);
          const img = item.portraitBannerUrl || item.landscapeBannerUrl || item.iconUrl;
          return (
            <button
              key={item.id}
              type="button"
              role="listitem"
              className="catalog-grid-card"
              onClick={() => onItemClick?.(item)}
              aria-label={label}
            >
              <div className="catalog-grid-card__img-wrap" style={img ? undefined : catalogPlaceholderStyle(item)}>
                {img ? (
                  <img src={img} alt="" className="catalog-grid-card__img" loading="lazy" />
                ) : (
                  <span className="catalog-grid-card__letter">{catalogInitial(item)}</span>
                )}
              </div>
              <div className="catalog-grid-card__label">{label}</div>
              {item.itemCount != null && item.itemCount > 0 && (
                <div className="catalog-grid-card__count">{item.itemCount} items</div>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default CatalogCategoryGrid;
